import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Clock, Calendar, Bell, Settings2, Save, Upload, CheckCircle, AlertCircle, Globe, DollarSign, Mail, Phone, MapPin, FileText, Server, Key, MessageSquare, Eye, EyeOff, Shield, HardDrive, RefreshCw, Play } from 'lucide-react';
import settingsService, { SystemSettings, DEFAULT_SETTINGS } from '../services/settingsService';
import api from '../services/api';

const Settings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'company' | 'workHours' | 'leave' | 'notifications' | 'system' | 'device' | 'hikvision' | 'payroll' | 'updates'>('company');
  const [hikSettings, setHikSettings] = useState<any>({ serverIp: '', serverPort: '80', endpointPath: '/api/hikvision/event-push', authType: 'none' });
  const [payrollFields, setPayrollFields] = useState<any[]>([]);
  const [updateInfo, setUpdateInfo] = useState<any>({ currentVersion: '1.1.57', lastCheck: null, latestVersion: null });
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showSmsSecret, setShowSmsSecret] = useState(false);

  useEffect(() => {
    loadSettings();
    loadHikvisionSettings();
  }, []);

  // 2026-08-14 (kullanici raporu: "Hikvision ayarlari kaydetmiyor")
  // KOK NEDEN: Kayit calisiyordu ancak sayfa acilirken KAYITLI DEGERLER OKUNMUYORDU;
  // alanlar hep bos geldigi icin kaydedilmemis gibi gorunuyordu.
  const loadHikvisionSettings = async () => {
    try {
      const r: any = await api.get('/settings/Hikvision');
      const raw = r?.data ?? r;
      const dict = (raw?.data ?? raw ?? {}) as Record<string, any>;
      if (!dict || Object.keys(dict).length === 0) return;
      setHikSettings({
        // Yeni anahtar adi oncelikli, eski ad geriye donuk uyum icin
        serverIp: dict.PushServerHost ?? dict.serverIp ?? '',
        serverPort: dict.PushServerPort ?? dict.serverPort ?? '80',
        endpointPath: dict.PushEndpointPath ?? dict.endpointPath ?? '/api/hikvision/event-push',
        authType: dict.PushAuthMethod ?? dict.authType ?? 'none',
      });
    } catch {
      // Ayar yoksa varsayilanlarla devam
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getAll();
      setSettings(data);
    } catch (error) {
      console.error('Settings load error:', error);
      showMessage('error', t('settings.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsService.update(settings as any);
      showMessage('success', t('settings.messages.saveSuccess'));
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || t('settings.messages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showMessage('error', t('settings.company.logoMaxSize'));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64 = reader.result as string;
        await settingsService.uploadLogo(base64);
        setSettings(prev => ({
          ...prev,
          company: { ...prev.company, Logo: base64 }
        }));
        showMessage('success', t('settings.messages.logoSuccess'));
      } catch (error: any) {
        showMessage('error', error.response?.data?.message || t('settings.messages.logoError'));
      }
    };
    reader.readAsDataURL(file);
  };

  const updateCompany = (key: keyof typeof settings.company, value: string) => {
    setSettings(prev => ({
      ...prev,
      company: { ...prev.company, [key]: value }
    }));
  };

  const updateWorkHours = (key: keyof typeof settings.workHours, value: string) => {
    setSettings(prev => ({
      ...prev,
      workHours: { ...prev.workHours, [key]: value }
    }));
  };

  const updateLeave = (key: keyof typeof settings.leave, value: string) => {
    setSettings(prev => ({
      ...prev,
      leave: { ...prev.leave, [key]: value }
    }));
  };

  const updateNotifications = (key: keyof typeof settings.notifications, value: string) => {
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value }
    }));
  };

  const updateSystem = (key: keyof typeof settings.system, value: string) => {
    setSettings(prev => ({
      ...prev,
      system: { ...prev.system, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'company', label: 'Firma Bilgileri', icon: Building2 },
    { id: 'workHours', label: 'Çalışma Saatleri', icon: Clock },
    { id: 'leave', label: 'İzin Ayarları', icon: Calendar },
    { id: 'notifications', label: 'Bildirimler', icon: Bell },
    { id: 'device', label: 'Cihaz Senkron', icon: HardDrive },
    { id: 'hikvision', label: 'Hikvision', icon: Server },
    { id: 'payroll', label: 'Bordro Parametreleri', icon: DollarSign },
    { id: 'updates', label: 'Güncellemeler', icon: RefreshCw },
    { id: 'system', label: 'Sistem', icon: Settings2 }
  ];

  const handleManualSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await api.post<{ synced: boolean }>('/devices/sync-all');
      if (result.success) {
        showMessage('success', result.message || t('settings.messages.syncSuccess'));
      } else {
        showMessage('error', result.message || t('settings.messages.syncFailed'));
      }
    } catch (error: any) {
      showMessage('error', error?.response?.data?.message || t('settings.messages.syncError'));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('settings.title')}</h1>
          <p className="text-slate-500 text-sm">{t('settings.subtitle')}</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-bold text-sm shadow-md active:scale-95 disabled:opacity-70 flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-b-transparent rounded-full animate-spin"></div>
              {t('settings.saving')}
            </>
          ) : (
            <>
              <Save size={16} /> {t('settings.save')}
            </>
          )}
        </button>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span className="text-sm font-medium">{message.text}</span>
          <button onClick={() => setMessage(null)} className="ml-auto text-current opacity-70 hover:opacity-100">&times;</button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-slate-100 flex overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-6 py-4 text-sm font-bold transition-all border-b-2 flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-blue-600 border-blue-600 bg-blue-50/50'
                  : 'text-slate-400 border-transparent hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-8">
          {/* Company Information */}
          {activeTab === 'company' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Logo */}
              <div className="flex items-start gap-6 pb-6 border-b border-slate-100">
                <div className="w-32 h-32 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50 overflow-hidden">
                  {settings.company.Logo ? (
                    <img src={settings.company.Logo} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Building2 size={48} className="text-slate-300" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-2">{t('settings.company.logo')}</h3>
                  <p className="text-sm text-slate-500 mb-4">{t('settings.company.logoHint')}</p>
                  <label className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all font-medium text-sm cursor-pointer inline-flex items-center gap-2">
                    <Upload size={16} /> {t('settings.company.uploadLogo')}
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.company.companyName')}</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="text"
                      value={settings.company.CompanyName}
                      onChange={(e) => updateCompany('CompanyName', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.company.phone')}</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="tel"
                      value={settings.company.CompanyPhone}
                      onChange={(e) => updateCompany('CompanyPhone', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      placeholder="+90 XXX XXX XX XX"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.company.email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="email"
                      value={settings.company.CompanyEmail}
                      onChange={(e) => updateCompany('CompanyEmail', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.company.website')}</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="url"
                      value={settings.company.CompanyWebsite}
                      onChange={(e) => updateCompany('CompanyWebsite', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.company.address')}</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 text-slate-400" size={18} />
                  <textarea
                    value={settings.company.CompanyAddress}
                    onChange={(e) => updateCompany('CompanyAddress', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm min-h-[80px]"
                    rows={3}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.company.taxNumber')}</label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="text"
                      value={settings.company.TaxNumber}
                      onChange={(e) => updateCompany('TaxNumber', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.company.taxOffice')}</label>
                  <input
                    type="text"
                    value={settings.company.TaxOffice}
                    onChange={(e) => updateCompany('TaxOffice', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Work Hours */}
          {activeTab === 'workHours' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.workHours.startTime')}</label>
                  <input
                    type="time"
                    value={settings.workHours.WorkStartTime}
                    onChange={(e) => updateWorkHours('WorkStartTime', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.workHours.endTime')}</label>
                  <input
                    type="time"
                    value={settings.workHours.WorkEndTime}
                    onChange={(e) => updateWorkHours('WorkEndTime', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.workHours.breakStart')}</label>
                  <input
                    type="time"
                    value={settings.workHours.BreakStartTime}
                    onChange={(e) => updateWorkHours('BreakStartTime', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.workHours.breakEnd')}</label>
                  <input
                    type="time"
                    value={settings.workHours.BreakEndTime}
                    onChange={(e) => updateWorkHours('BreakEndTime', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-slate-100">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.workHours.lateTolerance')}</label>
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={settings.workHours.LateToleranceMinutes}
                    onChange={(e) => updateWorkHours('LateToleranceMinutes', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">{t('settings.workHours.lateToleranceHint')}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.workHours.earlyLeaveTolerance')}</label>
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={settings.workHours.EarlyLeaveToleranceMinutes}
                    onChange={(e) => updateWorkHours('EarlyLeaveToleranceMinutes', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">{t('settings.workHours.earlyLeaveToleranceHint')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-slate-100">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.workHours.minReadingInterval')}</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={settings.workHours.MinReadingIntervalMinutes}
                    onChange={(e) => updateWorkHours('MinReadingIntervalMinutes', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">{t('settings.workHours.minReadingIntervalHint')}</p>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-100">
                <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Clock size={20} className="text-indigo-600" />
                    <div>
                      <span className="font-bold text-slate-800">{t('settings.workHours.onlyFirstLastEntry')}</span>
                      <p className="text-xs text-slate-500">{t('settings.workHours.onlyFirstLastEntryHint')}</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.workHours.OnlyFirstLastEntry === 'true'}
                    onChange={(e) => updateWorkHours('OnlyFirstLastEntry', e.target.checked ? 'true' : 'false')}
                    className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Leave Settings */}
          {activeTab === 'leave' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.leave.annualLeaveDays')}</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.leave.AnnualLeaveDays}
                    onChange={(e) => updateLeave('AnnualLeaveDays', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">{t('settings.leave.annualLeaveDaysHint')}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.leave.sickLeaveDays')}</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.leave.SickLeaveDays}
                    onChange={(e) => updateLeave('SickLeaveDays', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                </div>
              </div>

              <div className="pt-6 border-t border-slate-100">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.leave.RequireApproval === 'true'}
                    onChange={(e) => updateLeave('RequireApproval', e.target.checked ? 'true' : 'false')}
                    className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="font-bold text-slate-800">{t('settings.leave.requireApproval')}</span>
                    <p className="text-xs text-slate-500">{t('settings.leave.requireApprovalHint')}</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="space-y-8 animate-in fade-in duration-300">
              {/* Notification Preferences */}
              <div>
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Bell size={16} /> {t('settings.notifications.preferences')}
                </h3>
                <div className="space-y-4">
                  <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <Mail size={20} className="text-blue-600" />
                      <div>
                        <span className="font-bold text-slate-800">{t('settings.notifications.emailNotifications')}</span>
                        <p className="text-xs text-slate-500">{t('settings.notifications.emailNotificationsHint')}</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifications.EmailNotifications === 'true'}
                      onChange={(e) => updateNotifications('EmailNotifications', e.target.checked ? 'true' : 'false')}
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <Phone size={20} className="text-green-600" />
                      <div>
                        <span className="font-bold text-slate-800">{t('settings.notifications.smsNotifications')}</span>
                        <p className="text-xs text-slate-500">{t('settings.notifications.smsNotificationsHint')}</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifications.SMSNotifications === 'true'}
                      onChange={(e) => updateNotifications('SMSNotifications', e.target.checked ? 'true' : 'false')}
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <Clock size={20} className="text-orange-600" />
                      <div>
                        <span className="font-bold text-slate-800">{t('settings.notifications.lateNotifications')}</span>
                        <p className="text-xs text-slate-500">{t('settings.notifications.lateNotificationsHint')}</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifications.LateNotifications === 'true'}
                      onChange={(e) => updateNotifications('LateNotifications', e.target.checked ? 'true' : 'false')}
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <AlertCircle size={20} className="text-red-600" />
                      <div>
                        <span className="font-bold text-slate-800">{t('settings.notifications.absenceNotifications')}</span>
                        <p className="text-xs text-slate-500">{t('settings.notifications.absenceNotificationsHint')}</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.notifications.AbsenceNotifications === 'true'}
                      onChange={(e) => updateNotifications('AbsenceNotifications', e.target.checked ? 'true' : 'false')}
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                </div>
              </div>

              {/* Mail Server Settings */}
              <div className="pt-6 border-t border-slate-200">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Server size={16} /> {t('settings.notifications.smtpTitle')}
                </h3>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
                  <p className="text-sm text-blue-700 flex items-start gap-2">
                    <Shield size={16} className="mt-0.5 shrink-0" />
                    {t('settings.notifications.smtpDescription')}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.smtpServer')}</label>
                    <div className="relative">
                      <Server className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="text"
                        value={settings.notifications.SmtpHost}
                        onChange={(e) => updateNotifications('SmtpHost', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.port')}</label>
                    <input
                      type="number"
                      value={settings.notifications.SmtpPort}
                      onChange={(e) => updateNotifications('SmtpPort', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      placeholder="587"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.username')}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="email"
                        value={settings.notifications.SmtpUsername}
                        onChange={(e) => updateNotifications('SmtpUsername', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                        placeholder="noreply@yourcompany.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.password')}</label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type={showSmtpPassword ? 'text' : 'password'}
                        value={settings.notifications.SmtpPassword}
                        onChange={(e) => updateNotifications('SmtpPassword', e.target.value)}
                        className="w-full pl-10 pr-12 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                        placeholder="********"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showSmtpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.senderEmail')}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="email"
                        value={settings.notifications.SmtpFromEmail}
                        onChange={(e) => updateNotifications('SmtpFromEmail', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                        placeholder="noreply@yourcompany.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.senderName')}</label>
                    <input
                      type="text"
                      value={settings.notifications.SmtpFromName}
                      onChange={(e) => updateNotifications('SmtpFromName', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      placeholder={t('settings.notifications.senderNamePlaceholder')}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.notifications.SmtpUseSsl === 'true'}
                      onChange={(e) => updateNotifications('SmtpUseSsl', e.target.checked ? 'true' : 'false')}
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="font-bold text-slate-800">{t('settings.notifications.useSsl')}</span>
                      <p className="text-xs text-slate-500">{t('settings.notifications.useSslHint')}</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* SMS API Settings */}
              <div className="pt-6 border-t border-slate-200">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <MessageSquare size={16} /> {t('settings.notifications.smsTitle')}
                </h3>
                <div className="bg-green-50 border border-green-100 rounded-xl p-4 mb-4">
                  <p className="text-sm text-green-700 flex items-start gap-2">
                    <Shield size={16} className="mt-0.5 shrink-0" />
                    {t('settings.notifications.smsDescription')}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.smsProvider')}</label>
                    <select
                      value={settings.notifications.SmsProvider}
                      onChange={(e) => updateNotifications('SmsProvider', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                    >
                      <option value="netgsm">NetGSM</option>
                      <option value="iletimerkezi">Ileti Merkezi</option>
                      <option value="twilio">Twilio</option>
                      <option value="vonage">Vonage (Nexmo)</option>
                      <option value="custom">{t('settings.notifications.customApi')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.senderId')}</label>
                    <input
                      type="text"
                      value={settings.notifications.SmsSenderId}
                      onChange={(e) => updateNotifications('SmsSenderId', e.target.value)}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      placeholder="TempusPro"
                    />
                    <p className="text-xs text-slate-400 mt-1">{t('settings.notifications.senderIdHint')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.apiKey')}</label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type="text"
                        value={settings.notifications.SmsApiKey}
                        onChange={(e) => updateNotifications('SmsApiKey', e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                        placeholder={t('settings.notifications.apiKeyPlaceholder')}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.notifications.apiSecret')}</label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type={showSmsSecret ? 'text' : 'password'}
                        value={settings.notifications.SmsApiSecret}
                        onChange={(e) => updateNotifications('SmsApiSecret', e.target.value)}
                        className="w-full pl-10 pr-12 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                        placeholder="********"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSmsSecret(!showSmsSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showSmsSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Device Sync Settings */}
          {activeTab === 'device' && (
            <div className="space-y-8 animate-in fade-in duration-300">
              {/* Auto Sync */}
              <div>
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <RefreshCw size={16} /> {t('settings.device.autoFetchTitle')}
                </h3>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
                  <p className="text-sm text-blue-700 flex items-start gap-2">
                    <HardDrive size={16} className="mt-0.5 shrink-0" />
                    {t('settings.device.autoFetchDescription')}
                  </p>
                </div>

                <div className="space-y-4">
                  <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <RefreshCw size={20} className="text-blue-600" />
                      <div>
                        <span className="font-bold text-slate-800">{t('settings.device.autoSync')}</span>
                        <p className="text-xs text-slate-500">{t('settings.device.autoSyncHint')}</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.system.DeviceSyncEnabled === 'true'}
                      onChange={(e) => updateSystem('DeviceSyncEnabled', e.target.checked ? 'true' : 'false')}
                      className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.device.syncTime1')}</label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                          type="time"
                          value={settings.system.DeviceSyncTime1 || '07:00'}
                          onChange={(e) => updateSystem('DeviceSyncTime1', e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{t('settings.device.syncTime1Hint')}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.device.syncTime2')}</label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                          type="time"
                          value={settings.system.DeviceSyncTime2 || settings.system.DeviceSyncTime || '18:00'}
                          onChange={(e) => updateSystem('DeviceSyncTime2', e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{t('settings.device.syncTime2Hint')}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.device.duplicateFilter')}</label>
                      <input
                        type="number"
                        min="1"
                        max="60"
                        value={settings.system.DuplicateFilterMinutes || '5'}
                        onChange={(e) => updateSystem('DuplicateFilterMinutes', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      />
                      <p className="text-xs text-slate-400 mt-1">{t('settings.device.duplicateFilterHint')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Manual Sync */}
              <div className="pt-6 border-t border-slate-200">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Play size={16} /> {t('settings.device.manualSyncTitle')}
                </h3>
                <div className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-slate-800">{t('settings.device.syncNow')}</h4>
                      <p className="text-sm text-slate-500 mt-1">{t('settings.device.syncNowDescription')}</p>
                    </div>
                    <button
                      onClick={handleManualSync}
                      disabled={syncing}
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-bold text-sm shadow-md active:scale-95 disabled:opacity-70 flex items-center gap-2"
                    >
                      {syncing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-b-transparent rounded-full animate-spin"></div>
                          {t('settings.device.syncing')}
                        </>
                      ) : (
                        <>
                          <RefreshCw size={18} />
                          {t('settings.device.syncButton')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Last Sync Info */}
              <div className="pt-6 border-t border-slate-200">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">{t('settings.device.recentActions')}</h3>
                <p className="text-sm text-slate-500">{t('settings.device.recentActionsDescription')}</p>
              </div>
            </div>
          )}

          {/* HIKVISION Push Sunucu Ayarlari */}
          {activeTab === 'hikvision' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <Server size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-800">Hikvision Push Sunucu</h3>
                  <p className="text-slate-500 text-xs">Hikvision cihazları event'leri buraya POST eder. Cihaz arkasında NAT varsa public IP/port girin.</p>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-800">
                <span className="font-bold">ℹ Bilgi:</span> Burada belirlediğin <b>PDKS sunucu IP/port</b>, cihazlara <b>"Cihaza Push Host Yaz"</b> aksiyonu ile gönderilir. Cihaz bu adrese her yüz/kart event'inde POST atar.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">PDKS Sunucu IP / Hostname *</label>
                  <input type="text" value={hikSettings.serverIp} onChange={e => setHikSettings({...hikSettings, serverIp: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="orn. 192.168.1.10 veya pdks.firma.local" />
                  <p className="text-[10px] text-slate-400 mt-1">LAN ic IP veya public IP. NAT/port forwarding senaryosunda public.</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">PDKS Sunucu Port *</label>
                  <input type="text" value={hikSettings.serverPort} onChange={e => setHikSettings({...hikSettings, serverPort: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="80" />
                  <p className="text-[10px] text-slate-400 mt-1">Web sunucusunun dinleme portu (default 80, NAT'liyse forward'lanan port).</p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Endpoint Yolu</label>
                <input type="text" value={hikSettings.endpointPath} onChange={e => setHikSettings({...hikSettings, endpointPath: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="/api/hikvision/event-push" />
                <p className="text-[10px] text-slate-400 mt-1">Cihazın POST atacağı URL yolu (varsayılan: <code>/api/hikvision/event-push</code> — değiştirmesen daha iyi).</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">HTTP Kimlik Doğrulama</label>
                <select value={hikSettings.authType} onChange={e => setHikSettings({...hikSettings, authType: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
                  <option value="none">Yok (LAN icinde guvenli)</option>
                  <option value="basic">Basic Auth</option>
                  <option value="digest">Digest Auth</option>
                </select>
                <p className="text-[10px] text-slate-400 mt-1">Cihaz endpoint'e auth header eklerse seçin. LAN içinde none yeterli.</p>
              </div>
              <button
                onClick={async () => {
                  try {
                    // Backend PUT /settings/{group}/{key} bekliyor — her alani ayri ayri yaz
                    for (const [key, value] of Object.entries(hikSettings)) {
                      // 2026-08-14: Backend 'PushServerHost' vb. anahtarlari okuyor; panel ise
                      // 'serverIp' kaydediyordu -> cihaza push host YAZILAMIYORDU. Artik her iki
                      // ada da yaziyoruz (backend yeni adi oncelikli okur, eskisi geriye donuk uyum).
                      const anahtarEsleme: Record<string, string> = {
                        serverIp: 'PushServerHost',
                        serverPort: 'PushServerPort',
                        endpointPath: 'PushEndpointPath',
                        authType: 'PushAuthMethod',
                      };
                      const backendKey = anahtarEsleme[key];
                      await api.put(`/settings/Hikvision/${key}`, { value: String(value ?? ''), description: `Hikvision ${key}` });
                      if (backendKey) {
                        await api.put(`/settings/Hikvision/${backendKey}`, { value: String(value ?? ''), description: `Hikvision push ayari (${backendKey})` });
                      }
                    }
                    await loadHikvisionSettings();   // kaydedileni geri oku — dogrulama
                    showMessage('success', 'Hikvision ayarları kaydedildi ✓');
                  } catch (e: any) {
                    showMessage('error', e?.response?.data?.message || 'Kaydedilemedi');
                  }
                }}
                disabled={!hikSettings.serverIp}
                className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                <Save size={16} /> Kaydet {!hikSettings.serverIp && <span className="text-xs opacity-80">(Sunucu IP gerekli)</span>}
              </button>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600">
                <b>Sonraki adım:</b> Cihazlar sayfası → Hikvision protokollü cihazın satırında yeşil "Hikvision Aksiyonları" menüsü → <b>"Cihaza Push Host Yaz"</b>.
              </div>
            </div>
          )}

          {/* BORDRO PARAMETRELERI — Settings tablosu Category='Payroll' */}
          {activeTab === 'payroll' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <DollarSign size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-800">Bordro Parametreleri (2026)</h3>
                  <p className="text-slate-500 text-xs">Asgari ücret, SGK oranları, vergi dilimleri ve istisnalar — 6 ayda bir resmi değişikliğe açık.</p>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-800">
                <span className="font-bold">ℹ</span> Değer değişikliği sonrası <b>"Tümünü Kaydet"</b> butonuna tıklayın. Backend cache 15 dakika sonra otomatik yenilenir; <b>App Pool Recycle</b> ile anında etkili olur.
              </div>
              <PayrollSettingsSection api={api} showMessage={showMessage} />
            </div>
          )}

          {/* GUNCELLEMELER */}
          {activeTab === 'updates' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center">
                  <RefreshCw size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-800">Güncellemeler</h3>
                  <p className="text-slate-500 text-xs">Sistem sürüm bilgisi ve güncelleme kontrolü</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Mevcut Sürüm</p>
                  <p className="text-2xl font-black text-slate-800">{updateInfo.currentVersion || '1.1.57'}</p>
                  <p className="text-[10px] text-slate-500 mt-1">NexVera PDKS</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <p className="text-[10px] font-bold text-emerald-700 uppercase mb-1">Son Kontrol</p>
                  <p className="text-sm font-bold text-emerald-900">{updateInfo.lastCheck || 'Henüz kontrol edilmedi'}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-[10px] font-bold text-blue-700 uppercase mb-1">En Son Sürüm</p>
                  <p className="text-sm font-bold text-blue-900">{updateInfo.latestVersion || 'Bilinmiyor'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setUpdateInfo({ ...updateInfo, lastCheck: new Date().toLocaleString('tr-TR'), latestVersion: 'v1.1.57 (güncel)' });
                    showMessage('success', 'Güncelleme kontrol edildi — sisteminiz güncel');
                  }}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 flex items-center gap-2">
                  <RefreshCw size={16} /> Şimdi Kontrol Et
                </button>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
                <b>Otomatik güncelleme yakında.</b> Şu an için yeni sürümler manuel setup ile yüklenir: <code>installer_inno/output/NexVeraPDKS-Setup-*.exe</code>
              </div>
            </div>
          )}

          {/* System */}
          {activeTab === 'system' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.system.language')}</label>
                  <select
                    value={settings.system.Language}
                    onChange={(e) => updateSystem('Language', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  >
                    <option value="tr">Turkce</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.system.timezone')}</label>
                  <select
                    value={settings.system.Timezone}
                    onChange={(e) => updateSystem('Timezone', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  >
                    <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
                    <option value="Europe/London">Europe/London (UTC+0)</option>
                    <option value="America/New_York">America/New_York (UTC-5)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.system.dateFormat')}</label>
                  <select
                    value={settings.system.DateFormat}
                    onChange={(e) => updateSystem('DateFormat', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  >
                    <option value="dd.MM.yyyy">31.12.2024</option>
                    <option value="dd/MM/yyyy">31/12/2024</option>
                    <option value="yyyy-MM-dd">2024-12-31</option>
                    <option value="MM/dd/yyyy">12/31/2024</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.system.timeFormat')}</label>
                  <select
                    value={settings.system.TimeFormat}
                    onChange={(e) => updateSystem('TimeFormat', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  >
                    <option value="HH:mm">{t('settings.system.time24h')}</option>
                    <option value="hh:mm a">{t('settings.system.time12h')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.system.currency')}</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <select
                      value={settings.system.Currency}
                      onChange={(e) => updateSystem('Currency', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                    >
                      <option value="TRY">{t('settings.system.currencyTRY')}</option>
                      <option value="USD">US Dollar ($)</option>
                      <option value="EUR">Euro (EUR)</option>
                      <option value="GBP">British Pound (GBP)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('settings.system.sessionTimeout')}</label>
                  <input
                    type="number"
                    min="5"
                    max="480"
                    value={settings.system.SessionTimeout}
                    onChange={(e) => updateSystem('SessionTimeout', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">{t('settings.system.sessionTimeoutHint')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Settings tablosu Category='Payroll' key/value listesi — 25+ kanuni parametre
// SettingsApiController: GET /settings/{group} -> Dictionary<Key,Value>
// PUT /settings/single -> tek key guncelle
const PayrollSettingsSection: React.FC<{ api: any; showMessage: (t: 'success'|'error', m: string) => void }> = ({ api, showMessage }) => {
  const [rows, setRows] = React.useState<Array<{ key: string; value: string; description?: string }>>([]);
  const [loading, setLoading] = React.useState(false);
  const [dirty, setDirty] = React.useState<Record<string, string>>({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      // Backend her Category icin { Key: Value } dictionary donuyor. Ancak metadata (description) icin
      // ayri bir endpoint gerekmedigi surece Description gostermeyiz (frontend sadece key adiyla).
      const r: any = await api.get('/settings/Payroll');
      const dict = r?.data ?? r ?? {};
      const list = Object.entries(dict).map(([key, value]) => ({ key, value: String(value ?? ''), description: humanize(key) }));
      list.sort((a, b) => a.key.localeCompare(b.key));
      setRows(list);
      setDirty({});
    } catch (e: any) {
      showMessage('error', 'Bordro parametreleri yuklenemedi: ' + (e?.response?.data?.message || e?.message));
    } finally {
      setLoading(false);
    }
  }, [api, showMessage]);

  React.useEffect(() => { load(); }, [load]);

  // Kısa etiket + Detay açıklama
  const LABELS: Record<string, { title: string; desc: string }> = {
    AsgariUcret: { title: 'Asgari Ücret', desc: 'Aylık brüt asgari ücret (TL)' },
    AsgariUcret_DamgaVergisi_Istisna_Aylik: { title: 'Damga Vergisi İstisnası (Aylık)', desc: 'Asgari ücret üzerinden aylık damga vergisi istisnası' },
    AsgariUcret_GelirVergisi_Istisna_Aylik: { title: 'Gelir Vergisi İstisnası (Aylık)', desc: 'Asgari ücret üzerinden aylık gelir vergisi istisnası' },
    AylikStandartCalismaSaati: { title: 'Aylık Standart Çalışma Saati', desc: '30 gün × 7.5 saat = 225 saat/ay (yasal)' },
    AylikStandartGun: { title: 'Aylık Standart Çalışma Günü', desc: '30 gün (bordroda kullanılan)' },
    DamgaVergisiOrani: { title: 'Damga Vergisi Oranı', desc: '‰7,59 (binde yedi ellidokuz)' },
    FazlaMesaiCarpan: { title: 'Fazla Mesai Çarpanı', desc: '1.5 = %50 zamlı ücret (hafta içi mesai)' },
    FazlaMesaiPazarCarpan: { title: 'Pazar / Tatil Mesai Çarpanı', desc: '2.0 = %100 zamlı ücret (hafta sonu, resmi tatil)' },
    GelirVergisi_Dilim1_Limit: { title: 'Gelir Vergisi 1. Dilim Üst Sınırı', desc: 'TL cinsinden — bu tutara kadar %15' },
    GelirVergisi_Dilim1_Orani: { title: 'Gelir Vergisi 1. Dilim Oranı', desc: '%15' },
    GelirVergisi_Dilim2_Limit: { title: 'Gelir Vergisi 2. Dilim Üst Sınırı', desc: 'TL cinsinden — bu tutara kadar %20' },
    GelirVergisi_Dilim2_Orani: { title: 'Gelir Vergisi 2. Dilim Oranı', desc: '%20' },
    GelirVergisi_Dilim3_Limit: { title: 'Gelir Vergisi 3. Dilim Üst Sınırı', desc: 'TL cinsinden — bu tutara kadar %27' },
    GelirVergisi_Dilim3_Orani: { title: 'Gelir Vergisi 3. Dilim Oranı', desc: '%27' },
    GelirVergisi_Dilim4_Limit: { title: 'Gelir Vergisi 4. Dilim Üst Sınırı', desc: 'TL cinsinden — bu tutara kadar %35' },
    GelirVergisi_Dilim4_Orani: { title: 'Gelir Vergisi 4. Dilim Oranı', desc: '%35' },
    GelirVergisi_Dilim5_Orani: { title: 'Gelir Vergisi 5. Dilim Oranı', desc: '%40 (üst limit yok — 4. dilim üstü)' },
    IssizlikIsciOrani: { title: 'İşsizlik Sigortası (İşçi)', desc: '%1 — bordroda işçi kesintisi' },
    IssizlikIsverenOrani: { title: 'İşsizlik Sigortası (İşveren)', desc: '%2 — bordroda işveren maliyeti' },
    ParametreYili: { title: 'Parametre Yılı', desc: 'Bu parametrelerin geçerli olduğu yıl' },
    SgkIsciOrani: { title: 'SGK İşçi Primi', desc: '%14 — bordroda işçi kesintisi' },
    SgkIsverenOrani: { title: 'SGK İşveren Primi', desc: '%20.5 (5 puan teşvikle etkin %15.5)' },
    SgkIsverenTesvikOrani: { title: 'SGK 5 Puan İşveren Teşviki', desc: '%5 — asgari ücretli sigortalılarda otomatik uygulanır' },
    SgkTabanMatrah: { title: 'SGK Taban Matrahı', desc: 'Asgari ücret (bu tutarın altında SGK matrahı olamaz)' },
    SgkTavanMatrah: { title: 'SGK Tavan Matrahı', desc: '7.5 × asgari ücret (bu tutarın üstündeki gelirden SGK kesilmez)' },
  };
  const humanize = (k: string): string => LABELS[k]?.desc ?? '';
  const titleize = (k: string): string => LABELS[k]?.title ?? k;

  const setValue = (key: string, value: string) => {
    setDirty(d => ({ ...d, [key]: value }));
  };

  const saveAll = async () => {
    const entries = Object.entries(dirty);
    if (entries.length === 0) {
      showMessage('success', 'Değişiklik yok');
      return;
    }
    let ok = 0, err = 0;
    for (const [key, value] of entries) {
      try {
        await api.put(`/settings/Payroll/${key}`, { value: String(value ?? ''), description: humanize(key) });
        ok++;
      } catch (e) {
        err++;
      }
    }
    showMessage(err > 0 ? 'error' : 'success', `${ok} kayıt güncellendi${err > 0 ? `, ${err} hata` : ''}`);
    await load();
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-400 text-sm">Yükleniyor...</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-slate-400 text-sm space-y-2">
        <p>Bordro parametreleri bulunamadı.</p>
        <p className="text-[10px]">DB'de <code>Settings</code> tablosunda <code>Category='Payroll'</code> altında saklanır. Yeni kurulum <code>05_PAYROLL_2026.sql</code> ile 25+ kanuni değer seed'ler.</p>
        <button onClick={load} className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold">Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <div key={r.key} className="p-3 border border-slate-200 rounded-lg bg-slate-50">
            <label className="block text-xs font-bold text-slate-700 mb-1">{titleize(r.key)}</label>
            <input
              type="text"
              defaultValue={r.value}
              onChange={(e) => setValue(r.key, e.target.value)}
              className="w-full px-3 py-1.5 border border-slate-200 rounded text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
            />
            <p className="text-[10px] text-slate-500 mt-1">{r.description || <span className="opacity-50 font-mono">{r.key}</span>}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2 pt-3 border-t border-slate-200">
        <button
          onClick={saveAll}
          disabled={Object.keys(dirty).length === 0}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
          Tümünü Kaydet ({Object.keys(dirty).length})
        </button>
        <button
          onClick={() => { setDirty({}); load(); }}
          className="px-5 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50">
          Sıfırla
        </button>
      </div>
    </div>
  );
};

export default Settings;
